import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import moment from 'moment';
import { handleMaxMinFun, dataSourceToApi } from '@/utils/SwitchFun.js';

export const sort_items = [
  { text: 'Creator (Descending)', value: 'creator_count_desc' },
  { text: 'Creator (Ascending)', value: 'creator_count_asc' },
  { text: 'Total Products (Descending)', value: 'total_products_desc' },
  { text: 'Total Products (Ascending)', value: 'total_products_asc' },
  { text: 'Views (Descending)', value: 'views_desc' },
  { text: 'Views (Ascending)', value: 'views_asc' },
  { text: 'Clicks (Descending)', value: 'click_desc' },
  { text: 'Clicks (Ascending)', value: 'click_asc' },
  { text: 'Sales (Descending)', value: 'sales_desc' },
  { text: 'Sales (Ascending)', value: 'sales_asc' },
  { text: 'Sales Value (Descending)', value: 'sales_value_desc' },
  { text: 'Sales Value (Ascending)', value: 'sales_value_asc' },
  { text: 'Earnings (Descending)', value: 'earnings_desc' },
  { text: 'Earnings (Ascending)', value: 'earnings_asc' },
];

export const per_page_items = [
  { text: '50/page', value: 50 },
  { text: '100/page', value: 100 },
  { text: '200/page', value: 200 },
];

/**
 * @param {Object} options
 * @param {Function} options.buildStatisticUrl - ({ base, sortlink, keyword, curated }) => string
 * @param {Function} options.buildListUrl      - ({ base, page, per_page, sort_key, sort_val, sortlink, keyword, curated }) => string
 * @param {Boolean} options.useGlobalCreatorTags - 跟随 AnalyticsHeader 的 Creator Tag 过滤（页面级表格用；
 *   modal 内表格自带 tag 下拉并在 buildUrl 里拼 getTagParam()，不要开启，否则参数会重复）
 * @param {Boolean} options.includeStatistic - fetch buildStatisticUrl alongside the list on every
 *   refresh (default true). AdCreatorTable.vue passes false: it doesn't render data_statistic at
 *   all (unlike AdBrandTable/AdAdvertiserTable/AdBrandProductTable/AdProductTable, which all show
 *   the short-link summary row from it) — fetching it there was pure dead weight, and inside the
 *   Brand/Advertiser detail modals it duplicated that modal's own statistic call for the same
 *   endpoint (data_sta, feeding the tab's top summary cards).
 */
export function useAnalyticsProductList({
  buildStatisticUrl,
  buildListUrl,
  buildCsvUrl,
  useGlobalCreatorTags = false,
  includeStatistic = true,
}) {
  const store = useStore();
  const router = useRouter();
  const { proxy } = getCurrentInstance();
  const $ajax = proxy.$ajax;
  const EventBus = proxy.EventBus;

  const start_date = ref(moment().add(-30, 'days').toDate());
  const end_date = ref(moment().add(-1, 'days').toDate());
  const is_loading = ref(true);
  const data = ref([]);
  const data_statistic = ref(null);
const data_total = ref(null);
  const current_page = ref(1);
  const last_page = ref(1);
  const total = ref(0);
  const keyword = ref('');
  const brand_list = ref([]);
  const advertiser_list = ref([]);
  const creator_type = ref('');
  const product_added = ref('');
  const account_status = ref('');
  const curated_check = ref(false);
  const sortlink_data = ref('all');
  const sold_by = ref('');
  const selected_creator_tags = ref([]);
  const creator_tag_operation_type = ref('or');
  const per_page = ref(50);
  const sort = reactive({ sort_key: 'views', sort_val: 'desc' });
  const sort_index = ref(sort_items.findIndex((s) => s.value === `${sort.sort_key}_${sort.sort_val}`));

  const is_link_only = computed(() => store.state.user_data?.merchant_account?.platform_type === 'linkonly');
  const now_merchant = computed(() => store.state.user_data.merchant_account);

  const getApiBase = () => {
    const s = moment(start_date.value).format('YYYY-MM-DD');
    const e = moment(end_date.value).format('YYYY-MM-DD');
    return `start_date=${s}&end_date=${e}`;
  };

  const appendBrandIds = (api) => {
    let result = api;
    brand_list.value.forEach((brand) => {
      result += `&brand_ids[]=${brand.id}`;
    });
    advertiser_list.value.forEach((ad) => {
      result += `&advertiser_ids[]=${ad.id}`;
    });
    return result;
  };

  // 与 Products.vue 的 creator_tag_ids 拼法一致
  const appendCreatorTags = (api) => {
    if (!useGlobalCreatorTags || selected_creator_tags.value.length === 0) return api;
    let result = api;
    selected_creator_tags.value.forEach((tag) => {
      result += `&creator_tag_ids[]=${tag.id}`;
    });
    result += `&operation_type=${creator_tag_operation_type.value}`;
    return result;
  };

  const n = (v) => Number(v) || 0;

  const plusData = (da) => {
    const can_toggle_sortlink = da
      ? n(da.short_link_click_sum) + n(da.short_link_sales_sum) + n(da.short_link_sales_value_sum)
      : 0;
    if (!da) return { all_click: 0, all_sales: 0, all_sales_value: 0, all_earnings: 0, can_toggle_sortlink };
    if (sortlink_data.value === 'all') return {
      ...da,
      all_click: n(da.click_sum) + n(da.short_link_click_sum),
      all_sales: n(da.sales_sum) + n(da.short_link_sales_sum),
      all_sales_value: Math.ceil(n(da.sales_value_sum)) + Math.ceil(n(da.short_link_sales_value_sum)),
      all_earnings: Math.ceil(n(da.earnings_sum)) + Math.ceil(n(da.short_link_earnings_sum)),
      can_toggle_sortlink,
    };
    if (sortlink_data.value === 'shortlink') return {
      ...da,
      all_click: n(da.short_link_click_sum),
      all_sales: n(da.short_link_sales_sum),
      all_sales_value: Math.ceil(n(da.short_link_sales_value_sum)),
      all_earnings: Math.ceil(n(da.short_link_earnings_sum)),
      can_toggle_sortlink,
    };
    return {
      ...da,
      all_click: n(da.click_sum),
      all_sales: n(da.sales_sum),
      all_sales_value: Math.ceil(n(da.sales_value_sum)),
      all_earnings: Math.ceil(n(da.earnings_sum)),
      can_toggle_sortlink,
    };
  };

  const fetchStatistic = () => {
    if (!store.state.user_data.merchant_account_id) return;
    const api = appendCreatorTags(appendBrandIds(buildStatisticUrl({
      base: getApiBase(),
      sortlink: dataSourceToApi(sortlink_data.value),
      keyword: keyword.value,
      curated: curated_check.value ? '1' : '0',
      creator_type: creator_type.value,
      product_added: product_added.value,
      account_status: account_status.value,
      sold_by: sold_by.value,
    })));

    $ajax(api, { method: 'get', withCredentials: true }).then((res) => {
      data_statistic.value = plusData(res.data.data);
    });
  };

  const fetchList = () => {
    if (!store.state.user_data.merchant_account_id) return;
    is_loading.value = true;
    const api = appendCreatorTags(appendBrandIds(buildListUrl({
      base: getApiBase(),
      page: current_page.value,
      per_page: per_page.value,
      sort_key: sort.sort_key,
      sort_val: sort.sort_val,
      sortlink: dataSourceToApi(sortlink_data.value),
      keyword: keyword.value,
      curated: curated_check.value ? '1' : '0',
      creator_type: creator_type.value,
      product_added: product_added.value,
      account_status: account_status.value,
      sold_by: sold_by.value,
    })));
    $ajax(api, { method: 'get', withCredentials: true }).then((res) => {
      // 有些 API 回 { data: [...] }，有些回 { data: { data: [...], current_page, ... } }
      const paginator = (res.data?.data && !Array.isArray(res.data.data) && res.data.data?.current_page)
        ? res.data.data
        : res.data;
      const raw = paginator.data;
      data.value = Array.isArray(raw) ? raw.filter(i => i != null) : [];
      data_total.value = paginator.totals || null;
      current_page.value = Number(paginator.current_page) || 1;
      last_page.value = Number(paginator.last_page) || 1;
      total.value = Number(paginator.total) || 0;
      nextTick(() => { is_loading.value = false; });
    });
  };

  const fetchAll = () => {
    if (includeStatistic) fetchStatistic();
    fetchList();
  };

  // Download CSV using the current filter / sort (API returns the CSV file content directly)
  const fetchExportCsv = () => {
    const buildUrl = buildCsvUrl || buildListUrl;
    const api = appendCreatorTags(appendBrandIds(buildUrl({
      base: getApiBase(),
      page: current_page.value,
      per_page: per_page.value,
      sort_key: sort.sort_key,
      sort_val: sort.sort_val,
      sortlink: dataSourceToApi(sortlink_data.value),
      keyword: keyword.value,
      curated: curated_check.value ? '1' : '0',
      creator_type: creator_type.value,
      product_added: product_added.value,
      account_status: account_status.value,
      sold_by: sold_by.value,
    })));
    return $ajax(api, { method: 'get', withCredentials: true });
  };

  const onSearch = (val) => {
    keyword.value = val;
    current_page.value = 1;
    fetchAll();
  };

  const onBrandsChange = (brands) => {
    brand_list.value = brands;
    current_page.value = 1;
    fetchAll();
  };

  const onAdvertisersChange = (advertisers) => {
    advertiser_list.value = advertisers;
    current_page.value = 1;
    fetchAll();
  };

  const onCreatorTypeChange = (item) => {
    creator_type.value = item.value;
    current_page.value = 1;
    fetchAll();
  };

  const onProductAddedChange = (val) => {
    product_added.value = val;
    current_page.value = 1;
    fetchAll();
  };

  const onSoldByChange = (item) => {
    sold_by.value = item.value;
    current_page.value = 1;
    fetchAll();
  };

  const onAccountStatusChange = (val) => {
    account_status.value = val;
    current_page.value = 1;
    fetchAll();
  };

  const batchFilterUpdate = ({ creator_type: ct, product_added: pa, account_status: as } = {}) => {
    if (ct !== undefined) creator_type.value = ct;
    if (pa !== undefined) product_added.value = pa;
    if (as !== undefined) account_status.value = as;
    current_page.value = 1;
    fetchAll();
  };

  const onSortChange = (item) => {
    const lastUnderscore = item.value.lastIndexOf('_');
    sort.sort_key = item.value.slice(0, lastUnderscore);
    sort.sort_val = item.value.slice(lastUnderscore + 1);
    sort_index.value = sort_items.findIndex((s) => s.value === item.value);
    current_page.value = 1;
    fetchList();
  };

  const onColumnSort = (columnName) => {
    if (sort.sort_key === columnName) {
      sort.sort_val = sort.sort_val === 'asc' ? 'desc' : 'asc';
    } else {
      sort.sort_key = columnName;
      sort.sort_val = 'desc';
    }
    sort_index.value = sort_items.findIndex((s) => s.value === `${sort.sort_key}_${sort.sort_val}`);
    current_page.value = 1;
    fetchList();
  };

  const toggleCurated = () => {
    curated_check.value = !curated_check.value;
    current_page.value = 1;
    fetchAll();
  };

  const onPerPageChange = (item) => {
    per_page.value = item.value;
    current_page.value = 1;
    fetchList();
  };

  const gotoPage = (page) => {
    current_page.value = page;
    fetchList();
  };

  const startFunction = () => {
    sortlink_data.value = store.state.save_sortlink_data;
    if (store.state.save_analytics_start_date && store.state.save_analytics_end_date) {
      start_date.value = store.state.save_analytics_start_date;
      end_date.value = store.state.save_analytics_end_date;
    }
    // 换页进来时恢复 AnalyticsHeader 已选的 Creator Tag（与 Products.vue 一致）
    if (useGlobalCreatorTags && store.state.save_selected_tags?.length > 0) {
      selected_creator_tags.value = store.state.save_selected_tags;
      creator_tag_operation_type.value = store.state.save_tag_operation_type || 'or';
    }
    const checkTime = handleMaxMinFun(start_date.value, end_date.value, proxy);
    start_date.value = checkTime[0];
    end_date.value = checkTime[1];
    fetchAll();
  };

  const _onUpdateDate = (obj) => {
    start_date.value = obj.start_date;
    end_date.value = obj.end_date;
    current_page.value = 1;
    setTimeout(() => { fetchAll(); }, 10);
  };

  const _onUpdateSortlink = (value) => {
    sortlink_data.value = value;
    current_page.value = 1;
    fetchAll();
  };

  const _onTagsUpdated = (data) => {
    selected_creator_tags.value = data.tags || [];
    creator_tag_operation_type.value = data.operation_type || 'or';
    current_page.value = 1;
    fetchAll();
  };

  onMounted(() => {
    if (!store.state.user_data.permission_list.analytics.viewPerformanceSummaries.permission) {
      router.push('/creator/list');
      return;
    }
    startFunction();
    EventBus.on('update-date', _onUpdateDate);
    EventBus.on('update-sortlink', _onUpdateSortlink);
    if (useGlobalCreatorTags) EventBus.on('analytics-tags-updated', _onTagsUpdated);
  });

  onBeforeUnmount(() => {
    EventBus.off('update-date', _onUpdateDate);
    EventBus.off('update-sortlink', _onUpdateSortlink);
    if (useGlobalCreatorTags) EventBus.off('analytics-tags-updated', _onTagsUpdated);
  });

  return {
    is_loading, data, data_statistic, data_total,
    current_page, last_page, total,
    sort, sort_index, sortlink_data, curated_check,
    creator_type, product_added, account_status,
    is_link_only, now_merchant,
    onSearch, onSortChange, onColumnSort, onBrandsChange, onAdvertisersChange, onCreatorTypeChange,
    onProductAddedChange, onAccountStatusChange, onSoldByChange, batchFilterUpdate,
    toggleCurated, onPerPageChange, gotoPage,
    fetchExportCsv,
  };
}
